import { useState, useEffect } from 'react';
import axios from 'axios';
import { formatDate, getTodayISO, formatIndianCurrency } from '../utils/dateUtils';
import DateFilter, { filterByDateRange } from '../components/DateFilter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusColors = {
  'Sent': 'bg-blue-100 text-blue-800',
  'Received - Pending QC': 'bg-yellow-100 text-yellow-800',
  'QC In Progress': 'bg-purple-100 text-purple-800',
  'Partially Received': 'bg-yellow-100 text-yellow-800',
  'Partial Rework': 'bg-orange-100 text-orange-800',
  'Completed': 'bg-green-100 text-green-800',
  'Completed (Partial Reject)': 'bg-green-100 text-green-800',
  'Rejected': 'bg-red-100 text-red-800',
};

const qualityStatusColors = {
  'OK': 'bg-green-100 text-green-800',
  'Rejected': 'bg-red-100 text-red-800',
  'Rework': 'bg-orange-100 text-orange-800',
};

const jobWorkTypes = [
  'Threading', 'Hardening', 'Heat Treatment', 'Surface Grinding', 'Cylindrical Grinding', 'Polishing',
  'Plating', 'Powder Coating', 'Shot Blasting', 'Laser Cutting', 'Plasma Cutting', 'Forging', 'Blackening',
  'Painting', 'Welding', 'Machining', 'Assembly', 'Stamping', 'Other'
];

const unitOptions = ['pieces', 'kg', 'gm', 'meter', 'feet', 'inch', 'set', 'lot', 'nos', 'pair'];

export default function JobWorkChallans() {
  const [challans, setChallans] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [vendorItems, setVendorItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showReceiveModal, setShowReceiveModal] = useState(false);
  const [selectedChallan, setSelectedChallan] = useState(null);
  const [challanSummary, setChallanSummary] = useState(null);
  const [receiptHistory, setReceiptHistory] = useState([]);
  const [filterVendor, setFilterVendor] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [dateFilters, setDateFilters] = useState({ startDate: null, endDate: null });
  const [user, setUser] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Get current IST time in HH:MM format
  const getCurrentISTTime = () => {
    const now = new Date();
    const istOffset = 5.5 * 60 * 60 * 1000; // IST is UTC+5:30
    const istTime = new Date(now.getTime() + istOffset);
    return istTime.toISOString().slice(11, 16);
  };

  const [challanForm, setChallanForm] = useState({
    challan_number: '',
    company_id: '',
    vendor_id: '',
    vendor_name: '',
    challan_date: getTodayISO(),
    time_of_issue: getCurrentISTTime(),
    vehicle_no: '',
    job_work_period: 180,
    expected_return_date: '',
    remarks: ''
  });

  const [challanItems, setChallanItems] = useState([]);
  const [currentItem, setCurrentItem] = useState({
    partcode: '',
    description: '',
    item_type: 'Job Work',
    job_work_type: '',
    quantity: '',
    unit: 'pieces',
    material_type: 'Fresh',  // Fresh or Rework
    rate_per_piece: '',  // Optional price
    hsn_code: ''  // HSN/SAC code (mandatory)
  });

  // Receive material state
  const [receiveItems, setReceiveItems] = useState([]);
  const [receiveDate, setReceiveDate] = useState(getTodayISO());
  const [receiveRemarks, setReceiveRemarks] = useState('');

  useEffect(() => {
    fetchData();
    fetchUser();
  }, [filterVendor, filterStatus]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Error fetching user:', error);
    }
  };

  const fetchData = async () => {
    try {
      const [challansRes, vendorsRes, companiesRes] = await Promise.all([
        axios.get(`${API}/jobwork/challans`, {
          params: { vendor_id: filterVendor || undefined, status: filterStatus || undefined }
        }),
        axios.get(`${API}/jobwork/vendors`),
        axios.get(`${API}/companies`)
      ]);
      setChallans(challansRes.data);
      setVendors(vendorsRes.data);
      setCompanies(companiesRes.data);
      
      // Set default company
      if (companiesRes.data.length > 0 && !challanForm.company_id) {
        const defaultCompany = companiesRes.data.find(c => c.is_default) || companiesRes.data[0];
        setChallanForm(prev => ({ ...prev, company_id: defaultCompany.id }));
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchChallanSummary = async (challanId) => {
    try {
      const [summaryRes, historyRes] = await Promise.all([
        axios.get(`${API}/jobwork/challan-summary/${challanId}`),
        axios.get(`${API}/jobwork/challan-received/${challanId}`)
      ]);
      setChallanSummary(summaryRes.data);
      setReceiptHistory(historyRes.data);
    } catch (error) {
      console.error('Error fetching challan summary:', error);
    }
  };

  const fetchVendorItems = async (vendorId) => {
    try {
      const response = await axios.get(`${API}/jobwork/vendor-items/${vendorId}`);
      setVendorItems(response.data);
    } catch (error) {
      console.error('Error fetching vendor items:', error);
    }
  };

  const deleteChallan = async (challanId) => {
    const isAdmin = user?.role === 'admin';
    const confirmMsg = isAdmin 
      ? 'Are you sure you want to delete this challan? This action cannot be undone.'
      : 'Are you sure you want to request deletion of this challan? It will require admin approval.';
    
    if (!window.confirm(confirmMsg)) return;
    
    setDeleteLoading(true);
    try {
      const response = await axios.delete(`${API}/jobwork/challans/${challanId}`);
      
      if (response.data.requires_approval) {
        alert('Delete request submitted for admin approval.');
      } else {
        alert('Challan deleted successfully.');
      }
      
      setShowDetailsModal(false);
      setSelectedChallan(null);
      fetchData();
    } catch (error) {
      console.error('Error deleting challan:', error);
      alert(error.response?.data?.detail || 'Failed to delete challan');
    } finally {
      setDeleteLoading(false);
    }
  };

  const fetchNextChallanNumber = async (companyId) => {
    try {
      const response = await axios.get(`${API}/jobwork/challan-number/next`, {
        params: { company_id: companyId }
      });
      return response.data.challan_number;
    } catch (error) {
      console.error('Error fetching challan number:', error);
      return '';
    }
  };

  const handleCompanyChange = async (e) => {
    const companyId = e.target.value;
    const newChallanNumber = companyId ? await fetchNextChallanNumber(companyId) : '';
    setChallanForm(prev => ({ ...prev, company_id: companyId, challan_number: newChallanNumber }));
  };

  const handleVendorChange = async (e) => {
    const vendorId = e.target.value;
    const vendor = vendors.find(v => v.id === vendorId);
    setChallanForm(prev => ({
      ...prev,
      vendor_id: vendorId,
      vendor_name: vendor ? vendor.name : ''
    }));
    if (vendorId) {
      await fetchVendorItems(vendorId);
    } else {
      setVendorItems([]);
    }
  };

  const handleItemFromCatalog = (e) => {
    const itemId = e.target.value;
    const item = vendorItems.find(i => i.id === itemId);
    if (item) {
      setCurrentItem({
        partcode: item.partcode,
        description: item.description,
        item_type: item.item_type,
        job_work_type: item.job_work_type || '',
        quantity: '',
        unit: item.unit || 'pieces',
        material_type: 'Fresh',
        rate_per_piece: item.rate_per_piece || '',
        hsn_code: item.hsn_code || ''
      });
    }
  };

  const addItemToChallan = () => {
    if (!currentItem.partcode || !currentItem.quantity) {
      alert('Please fill in partcode and quantity');
      return;
    }
    if (!currentItem.hsn_code) {
      alert('HSN/SAC Code is mandatory. Please enter it before adding item.');
      return;
    }
    const newItem = {
      ...currentItem,
      quantity: parseInt(currentItem.quantity),
      rate_per_piece: currentItem.rate_per_piece ? parseFloat(currentItem.rate_per_piece) : null,
      unit: currentItem.unit || 'pieces',
      hsn_code: currentItem.hsn_code
    };
    setChallanItems([...challanItems, newItem]);
    setCurrentItem({
      partcode: '',
      description: '',
      item_type: 'Job Work',
      job_work_type: '',
      quantity: '',
      unit: 'pieces',
      material_type: 'Fresh',
      rate_per_piece: '',
      hsn_code: ''
    });
  };

  const removeItem = (index) => {
    setChallanItems(challanItems.filter((_, i) => i !== index));
  };

  const openCreateModal = async () => {
    const defaultCompany = companies.find(c => c.is_default) || companies[0];
    const companyId = defaultCompany?.id || '';
    const nextChallanNumber = companyId ? await fetchNextChallanNumber(companyId) : '';
    setChallanForm({
      challan_number: nextChallanNumber,
      company_id: companyId,
      vendor_id: '',
      vendor_name: '',
      challan_date: getTodayISO(),
      time_of_issue: getCurrentISTTime(),
      vehicle_no: '',
      job_work_period: 180,
      expected_return_date: '',
      remarks: ''
    });
    setChallanItems([]);
    setShowCreateModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (challanItems.length === 0) {
      alert('Please add at least one item');
      return;
    }
    if (!challanForm.company_id) {
      alert('Please select a company');
      return;
    }
    try {
      await axios.post(`${API}/jobwork/challans`, {
        ...challanForm,
        challan_number: challanForm.challan_number || null,
        items: challanItems
      });
      setShowCreateModal(false);
      fetchData();
    } catch (error) {
      console.error('Error creating challan:', error);
      alert('Error creating challan: ' + (error.response?.data?.detail || error.message));
    }
  };

  const viewChallanDetails = async (challan) => {
    try {
      const response = await axios.get(`${API}/jobwork/challans/${challan.id}`);
      setSelectedChallan(response.data);
      await fetchChallanSummary(challan.id);
      setShowDetailsModal(true);
    } catch (error) {
      console.error('Error fetching challan details:', error);
    }
  };

  const openReceiveModal = async (challan) => {
    try {
      const response = await axios.get(`${API}/jobwork/challans/${challan.id}`);
      setSelectedChallan(response.data);
      await fetchChallanSummary(challan.id);
      
      // Initialize receive items from challan items (for pending receipt)
      const challanData = response.data;
      const pendingItems = challanData.items
        .map(item => ({
          partcode: item.partcode,
          description: item.description,
          sent_qty: item.quantity,
          bill_quantity: item.quantity.toString(),  // Default to sent qty
          actual_quantity: '',
          remarks: ''
        }));
      setReceiveItems(pendingItems);
      
      setReceiveDate(getTodayISO());
      setReceiveRemarks('');
      setShowReceiveModal(true);
    } catch (error) {
      console.error('Error opening receive modal:', error);
    }
  };

  const handleReceiveSubmit = async (e) => {
    e.preventDefault();
    
    // Filter items with actual quantity entered
    const itemsToReceive = receiveItems
      .filter(item => item.actual_quantity && parseInt(item.actual_quantity) > 0)
      .map(item => ({
        partcode: item.partcode,
        description: item.description,
        bill_quantity: parseInt(item.bill_quantity) || 0,
        actual_quantity: parseInt(item.actual_quantity),
        remarks: item.remarks
      }));
    
    if (itemsToReceive.length === 0) {
      alert('Please enter quantity for at least one item');
      return;
    }
    
    try {
      await axios.post(`${API}/jobwork/challan-received`, {
        challan_id: selectedChallan.id,
        items: itemsToReceive,
        received_date: receiveDate,
        remarks: receiveRemarks
      });
      
      setShowReceiveModal(false);
      fetchData();
      
      // Refresh the details modal if it's open
      if (showDetailsModal) {
        await fetchChallanSummary(selectedChallan.id);
        const response = await axios.get(`${API}/jobwork/challans/${selectedChallan.id}`);
        setSelectedChallan(response.data);
      }
    } catch (error) {
      console.error('Error recording receipt:', error);
      alert('Error recording receipt: ' + (error.response?.data?.detail || error.message));
    }
  };

  const updateReceiveItem = (index, field, value) => {
    const updated = [...receiveItems];
    updated[index][field] = value;
    setReceiveItems(updated);
  };

  const downloadPDF = async (challanId) => {
    try {
      const response = await axios.get(`${API}/jobwork/challans/${challanId}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `challan_${challanId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Error downloading PDF');
    }
  };

  const updateStatus = async (challanId, newStatus) => {
    try {
      await axios.put(`${API}/jobwork/challans/${challanId}/status?status=${newStatus}`);
      fetchData();
      if (showDetailsModal) {
        const response = await axios.get(`${API}/jobwork/challans/${challanId}`);
        setSelectedChallan(response.data);
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  // Apply date filter
  const filteredChallans = filterByDateRange(challans, 'challan_date', dateFilters.startDate, dateFilters.endDate);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Job Work Challans</h1>
          <p className="text-gray-600 mt-1">Daily job work without PO</p>
        </div>
        <button
          onClick={openCreateModal}
          className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg transition"
        >
          + Create Challan
        </button>
      </div>

      {/* Date Filter */}
      <DateFilter onFilterChange={setDateFilters} />

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow flex gap-4 flex-wrap">
        <select
          value={filterVendor}
          onChange={(e) => setFilterVendor(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg"
        >
          <option value="">All Vendors</option>
          {vendors.map(v => (
            <option key={v.id} value={v.id}>{v.name}</option>
          ))}
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg"
        >
          <option value="">All Status</option>
          <option value="Sent">Sent</option>
          <option value="Partially Received">Partially Received</option>
          <option value="Completed">Completed</option>
        </select>
      </div>

      {/* Challans Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Challan No</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendor</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Items</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Value</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filteredChallans.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                  No challans found. Create your first challan!
                </td>
              </tr>
            ) : (
              filteredChallans.map(challan => {
                const totalValue = challan.items?.reduce((sum, item) => 
                  sum + (item.rate_per_piece ? item.quantity * item.rate_per_piece : 0), 0) || 0;
                return (
                  <tr key={challan.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium text-gray-900">{challan.challan_number}</td>
                    <td className="px-6 py-4 text-gray-600">{formatDate(challan.challan_date)}</td>
                    <td className="px-6 py-4 text-gray-600">{challan.vendor_name}</td>
                    <td className="px-6 py-4 text-gray-600">{challan.items?.length || 0} items</td>
                    <td className="px-6 py-4 text-right font-medium text-green-700">
                      {totalValue > 0 ? formatIndianCurrency(totalValue) : '-'}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs ${statusColors[challan.status] || 'bg-gray-100'}`}>
                        {challan.status}
                      </span>
                    </td>
                  <td className="px-6 py-4 space-x-2">
                    <button
                      onClick={() => viewChallanDetails(challan)}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      View
                    </button>
                    <button
                      onClick={() => downloadPDF(challan.id)}
                      className="text-green-600 hover:text-green-800"
                    >
                      PDF
                    </button>
                    {challan.status !== 'Completed' && (
                      <button
                        onClick={() => openReceiveModal(challan)}
                        className="text-purple-600 hover:text-purple-800"
                      >
                        Receive
                      </button>
                    )}
                  </td>
                </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Create Challan Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900">Create Job Work Challan</h2>
                <button onClick={() => setShowCreateModal(false)} className="text-gray-500 hover:text-gray-700">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Company & Challan Header */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company *</label>
                  <select
                    value={challanForm.company_id}
                    onChange={handleCompanyChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    required
                  >
                    <option value="">Select Company</option>
                    {companies.map(company => (
                      <option key={company.id} value={company.id}>
                        {company.name} ({company.code})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Challan Number</label>
                  <input
                    type="text"
                    value={challanForm.challan_number}
                    onChange={(e) => setChallanForm({ ...challanForm, challan_number: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="Auto-generated or enter custom"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Challan Date *</label>
                  <input
                    type="date"
                    value={challanForm.challan_date}
                    onChange={(e) => setChallanForm({ ...challanForm, challan_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Time of Issue (IST) *</label>
                  <input
                    type="time"
                    value={challanForm.time_of_issue}
                    onChange={(e) => setChallanForm({ ...challanForm, time_of_issue: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Vendor *</label>
                  <select
                    value={challanForm.vendor_id}
                    onChange={handleVendorChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    required
                  >
                    <option value="">Select Vendor</option>
                    {vendors.map(vendor => (
                      <option key={vendor.id} value={vendor.id}>{vendor.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle No.</label>
                  <input
                    type="text"
                    value={challanForm.vehicle_no}
                    onChange={(e) => setChallanForm({ ...challanForm, vehicle_no: e.target.value.toUpperCase() })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="e.g., PB-65-1234"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Job Work Period (Days) *</label>
                  <input
                    type="number"
                    value={challanForm.job_work_period}
                    onChange={(e) => setChallanForm({ ...challanForm, job_work_period: parseInt(e.target.value) || 180 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    min="1"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Expected Return</label>
                  <input
                    type="date"
                    value={challanForm.expected_return_date}
                    onChange={(e) => setChallanForm({ ...challanForm, expected_return_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>

              {/* Add Item Section */}
              <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                <h3 className="font-semibold text-gray-900">Add Items</h3>
                
                {challanForm.vendor_id && vendorItems.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Quick Select from Catalog</label>
                    <select onChange={handleItemFromCatalog} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                      <option value="">Select from vendor items...</option>
                      {vendorItems.map(item => (
                        <option key={item.id} value={item.id}>{item.partcode} - {item.description}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <input
                    type="text"
                    placeholder="Part Code *"
                    value={currentItem.partcode}
                    onChange={(e) => setCurrentItem({ ...currentItem, partcode: e.target.value.toUpperCase() })}
                    className="px-3 py-2 border border-gray-300 rounded-lg"
                  />
                  <input
                    type="text"
                    placeholder="Description"
                    value={currentItem.description}
                    onChange={(e) => setCurrentItem({ ...currentItem, description: e.target.value })}
                    className="px-3 py-2 border border-gray-300 rounded-lg"
                  />
                  <select
                    value={currentItem.job_work_type}
                    onChange={(e) => setCurrentItem({ ...currentItem, job_work_type: e.target.value })}
                    className="px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="">Job Type</option>
                    {jobWorkTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                  <input
                    type="number"
                    placeholder="Qty *"
                    value={currentItem.quantity}
                    onChange={(e) => setCurrentItem({ ...currentItem, quantity: e.target.value })}
                    className="px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                
                {/* Second row for Material Type, Unit, Price */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                  <select
                    value={currentItem.material_type}
                    onChange={(e) => setCurrentItem({ ...currentItem, material_type: e.target.value })}
                    className={`px-3 py-2 border rounded-lg ${
                      currentItem.material_type === 'Rework' 
                        ? 'border-orange-400 bg-orange-50 text-orange-800' 
                        : 'border-gray-300'
                    }`}
                  >
                    <option value="Fresh">Fresh Material</option>
                    <option value="Rework">Rework</option>
                  </select>
                  <select
                    value={currentItem.unit}
                    onChange={(e) => setCurrentItem({ ...currentItem, unit: e.target.value })}
                    className="px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    {unitOptions.map(unit => (
                      <option key={unit} value={unit}>{unit}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    placeholder="HSN/SAC Code *"
                    value={currentItem.hsn_code}
                    onChange={(e) => setCurrentItem({ ...currentItem, hsn_code: e.target.value })}
                    className="px-3 py-2 border border-red-300 rounded-lg bg-red-50"
                    required
                  />
                  <input
                    type="number"
                    step="0.01"
                    placeholder="Rate/Price (Optional)"
                    value={currentItem.rate_per_piece}
                    onChange={(e) => setCurrentItem({ ...currentItem, rate_per_piece: e.target.value })}
                    className="px-3 py-2 border border-gray-300 rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={addItemToChallan}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
                  >
                    + Add Item
                  </button>
                </div>
              </div>

              {/* Items List */}
              {challanItems.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Part Code</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Description</th>
                        <th className="px-4 py-2 text-center text-xs font-medium text-gray-500">HSN</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Job Type</th>
                        <th className="px-4 py-2 text-center text-xs font-medium text-gray-500">Type</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Qty</th>
                        <th className="px-4 py-2 text-center text-xs font-medium text-gray-500">Unit</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Rate</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Amount</th>
                        <th className="px-4 py-2 text-center text-xs font-medium text-gray-500">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {challanItems.map((item, idx) => (
                        <tr key={idx} className={item.material_type === 'Rework' ? 'bg-orange-50' : ''}>
                          <td className="px-4 py-2 font-medium">{item.partcode}</td>
                          <td className="px-4 py-2">{item.description}</td>
                          <td className="px-4 py-2 text-center text-xs">{item.hsn_code || '-'}</td>
                          <td className="px-4 py-2">{item.job_work_type || '-'}</td>
                          <td className="px-4 py-2 text-center">
                            <span className={`px-2 py-0.5 rounded-full text-xs ${
                              item.material_type === 'Rework' 
                                ? 'bg-orange-100 text-orange-800' 
                                : 'bg-green-100 text-green-800'
                            }`}>
                              {item.material_type}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-right">{item.quantity}</td>
                          <td className="px-4 py-2 text-center text-xs">{item.unit}</td>
                          <td className="px-4 py-2 text-right">
                            {item.rate_per_piece ? formatIndianCurrency(item.rate_per_piece) : '-'}
                          </td>
                          <td className="px-4 py-2 text-right">
                            {item.rate_per_piece ? formatIndianCurrency(item.quantity * item.rate_per_piece) : '-'}
                          </td>
                          <td className="px-4 py-2 text-center">
                            <button type="button" onClick={() => removeItem(idx)} className="text-red-600 hover:text-red-800">
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    {/* Total Row */}
                    <tfoot className="bg-gray-100">
                      <tr>
                        <td colSpan="5" className="px-4 py-2 text-right font-semibold">Total:</td>
                        <td className="px-4 py-2 text-right font-semibold">
                          {challanItems.reduce((sum, item) => sum + item.quantity, 0)}
                        </td>
                        <td className="px-4 py-2 text-right"></td>
                        <td className="px-4 py-2 text-right"></td>
                        <td className="px-4 py-2 text-right font-semibold text-green-700">
                          {formatIndianCurrency(challanItems.reduce((sum, item) => sum + (item.rate_per_piece ? item.quantity * item.rate_per_piece : 0), 0))}
                        </td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}

              {/* Remarks */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                <textarea
                  value={challanForm.remarks}
                  onChange={(e) => setChallanForm({ ...challanForm, remarks: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  rows="2"
                />
              </div>

              {/* Submit */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                  disabled={challanItems.length === 0}
                >
                  Create Challan
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Challan Details Modal */}
      {showDetailsModal && selectedChallan && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[85vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">{selectedChallan.challan_number}</h2>
                  <span className={`px-2 py-1 rounded-full text-xs ${statusColors[selectedChallan.status]}`}>
                    {selectedChallan.status}
                  </span>
                </div>
                <button onClick={() => setShowDetailsModal(false)} className="text-gray-500 hover:text-gray-700">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="p-6 space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 bg-gray-50 p-4 rounded-lg">
                <div>
                  <p className="text-sm text-gray-600">Vendor</p>
                  <p className="font-medium">{selectedChallan.vendor_name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Challan Date</p>
                  <p className="font-medium">{formatDate(selectedChallan.challan_date)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Expected Return</p>
                  <p className="font-medium">{formatDate(selectedChallan.expected_return_date) || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Receipts</p>
                  <p className="font-medium">{receiptHistory.length} records</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total Value</p>
                  <p className="font-medium text-green-700">
                    {(() => {
                      const totalValue = selectedChallan.items?.reduce((sum, item) => 
                        sum + (item.rate_per_piece ? item.quantity * item.rate_per_piece : 0), 0) || 0;
                      return totalValue > 0 ? formatIndianCurrency(totalValue) : '-';
                    })()}
                  </p>
                </div>
              </div>

              {/* Items Summary with Sent vs Received */}
              <div>
                <h3 className="font-semibold mb-3">Material Status</h3>
                <div className="border rounded-lg overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Part Code</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                        <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Type</th>
                        <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Sent</th>
                        <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Received</th>
                        <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Pending</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Rate</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                        <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {challanSummary?.items?.map((item, idx) => (
                        <tr key={idx} className={item.material_type === 'Rework' ? 'bg-orange-50' : item.receipt_complete ? 'bg-green-50' : item.actual_received > 0 ? 'bg-yellow-50' : ''}>
                          <td className="px-4 py-2 font-medium">{item.partcode}</td>
                          <td className="px-4 py-2 text-sm">{item.description}</td>
                          <td className="px-4 py-2 text-center">
                            <span className={`px-2 py-0.5 rounded-full text-xs ${
                              item.material_type === 'Rework' 
                                ? 'bg-orange-100 text-orange-800' 
                                : 'bg-green-100 text-green-800'
                            }`}>
                              {item.material_type || 'Fresh'}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-center">{item.sent_quantity}</td>
                          <td className="px-4 py-2 text-center font-medium text-green-700">{item.actual_received}</td>
                          <td className="px-4 py-2 text-center font-medium text-orange-600">{item.pending_receipt}</td>
                          <td className="px-4 py-2 text-right">{item.rate_per_piece ? formatIndianCurrency(item.rate_per_piece) : '-'}</td>
                          <td className="px-4 py-2 text-right">
                            {item.rate_per_piece ? formatIndianCurrency(item.sent_quantity * item.rate_per_piece) : '-'}
                          </td>
                          <td className="px-4 py-2 text-center">
                            {item.receipt_complete ? (
                              <span className="text-green-600">✓ Complete</span>
                            ) : item.actual_received > 0 ? (
                              <span className="text-yellow-600">Partial</span>
                            ) : (
                              <span className="text-gray-500">Pending</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Receipt History */}
              {receiptHistory.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-3">Receipt History</h3>
                  <div className="space-y-3">
                    {receiptHistory.map((receipt, idx) => (
                      <div key={idx} className="border rounded-lg p-4 bg-gray-50">
                        <div className="flex justify-between items-center mb-2">
                          <span className="font-medium text-gray-900">
                            📦 Receipt on {formatDate(receipt.received_date)}
                          </span>
                          <span className="text-sm text-gray-500">by {receipt.received_by}</span>
                        </div>
                        <div className="space-y-1">
                          {receipt.items?.map((item, i) => (
                            <div key={i} className="flex justify-between text-sm">
                              <span>{item.partcode} - {item.description}</span>
                              <span className="flex items-center gap-2">
                                <span className="font-medium">{item.quantity_received} pcs</span>
                                <span className={`px-2 py-0.5 rounded text-xs ${qualityStatusColors[item.quality_status] || 'bg-gray-100'}`}>
                                  {item.quality_status}
                                </span>
                              </span>
                            </div>
                          ))}
                        </div>
                        {receipt.remarks && (
                          <p className="text-sm text-gray-600 mt-2 italic">Note: {receipt.remarks}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedChallan.remarks && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">Challan Remarks</p>
                  <p className="font-medium">{selectedChallan.remarks}</p>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-between gap-3 pt-4 border-t">
                <button
                  onClick={() => deleteChallan(selectedChallan.id)}
                  disabled={deleteLoading}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {deleteLoading ? 'Processing...' : (user?.role === 'admin' ? 'Delete' : 'Request Delete')}
                </button>
                <div className="flex gap-3">
                  <button
                    onClick={() => downloadPDF(selectedChallan.id)}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    Download PDF
                  </button>
                  {selectedChallan.status !== 'Completed' && (
                    <button
                      onClick={() => {
                        setShowDetailsModal(false);
                        openReceiveModal(selectedChallan);
                      }}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                    >
                      Receive Material
                    </button>
                  )}
                  <button
                    onClick={() => setShowDetailsModal(false)}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Receive Material Modal */}
      {showReceiveModal && selectedChallan && challanSummary && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[85vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Receive Material</h2>
                  <p className="text-gray-600">{selectedChallan.challan_number} - {selectedChallan.vendor_name}</p>
                </div>
                <button onClick={() => setShowReceiveModal(false)} className="text-gray-500 hover:text-gray-700">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <form onSubmit={handleReceiveSubmit} className="p-6 space-y-6">
              {/* Receipt Date */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Receipt Date *</label>
                  <input
                    type="date"
                    value={receiveDate}
                    onChange={(e) => setReceiveDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    required
                  />
                </div>
              </div>

              {/* Info box */}
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                💡 <strong>Note:</strong> Enter the quantity as per bill/challan and the actual counted quantity. QC inspection will be done separately after receipt.
              </div>

              {/* Items to Receive */}
              <div>
                <h3 className="font-semibold mb-3">Enter Received Quantities</h3>
                <div className="border rounded-lg overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Part Code</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                        <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Sent Qty</th>
                        <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Bill Qty</th>
                        <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Actual Qty</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Remarks</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {receiveItems.map((item, idx) => (
                        <tr key={idx}>
                          <td className="px-4 py-2 font-medium">{item.partcode}</td>
                          <td className="px-4 py-2 text-sm">{item.description}</td>
                          <td className="px-4 py-2 text-center text-gray-600">{item.sent_qty}</td>
                          <td className="px-4 py-2">
                            <input
                              type="number"
                              min="0"
                              value={item.bill_quantity}
                              onChange={(e) => updateReceiveItem(idx, 'bill_quantity', e.target.value)}
                              className="w-20 px-2 py-1 border border-gray-300 rounded text-center"
                            />
                          </td>
                          <td className="px-4 py-2">
                            <input
                              type="number"
                              min="0"
                              value={item.actual_quantity}
                              onChange={(e) => updateReceiveItem(idx, 'actual_quantity', e.target.value)}
                              className="w-20 px-2 py-1 border border-blue-300 rounded text-center bg-blue-50"
                              placeholder="Count"
                            />
                            {item.actual_quantity && item.bill_quantity && parseInt(item.actual_quantity) !== parseInt(item.bill_quantity) && (
                              <span className={`text-xs block mt-1 ${parseInt(item.actual_quantity) > parseInt(item.bill_quantity) ? 'text-green-600' : 'text-red-600'}`}>
                                {parseInt(item.actual_quantity) > parseInt(item.bill_quantity) ? '+' : ''}{parseInt(item.actual_quantity) - parseInt(item.bill_quantity)}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-2">
                            <input
                              type="text"
                              value={item.remarks || ''}
                              onChange={(e) => updateReceiveItem(idx, 'remarks', e.target.value)}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                              placeholder="Optional"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {challanSummary.items?.filter(item => item.pending_receipt > 0).length === 0 && (
                  <p className="text-center py-4 text-green-600 font-medium">
                    ✓ All items have been received!
                  </p>
                )}
              </div>

              {/* Remarks */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Receipt Remarks</label>
                <textarea
                  value={receiveRemarks}
                  onChange={(e) => setReceiveRemarks(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  rows="2"
                  placeholder="Optional notes for this receipt..."
                />
              </div>

              {/* Submit */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowReceiveModal(false)}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                  disabled={challanSummary.items?.filter(item => item.pending_receipt > 0).length === 0}
                >
                  Record Receipt
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
